import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import theme from "theme";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import { makeStyles } from "@mui/styles";
import { Dialog, IconButton, Paper, Tooltip } from "@mui/material";
import { IconButton as CustomIconButton } from "components/CustomButtons";
import { useNavigate } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';

import DynamicTable from "components/DynamicTable";
import {
  deleteDraftById,
  getBatches,
  getDomains,
  getPersonalizedInterview,
} from "services";
import { BootstrapTooltip } from "components/InfoIconWithTooltip";
import { Session, copyToClipboard, formatDate, getClientPrefix } from 'utils';
import { applyFiltersByHistory } from "utils";
import useEditorStore from "editors/TemplateEditor/editorStore";
import { useSnackbar } from "contexts";
import { createChallenge, deleteChallenge, getCodingInterviews } from "services/codeChallengeService";
import { useServices } from "contexts/SingleSessionProvider";
import { Header, Proctorship } from "screens/CreateCodingInterview";
import { PublishingComponent, Header as ShortlistHeader } from "components/CreateShortListTask";
import { CaseMasterPublishingSteps, MockPublishingSteps, TemplateType } from "./constant";
import { createShortlistTask, deleteShortlistTaskById, getShortlistingTasks } from "services/shortlistingTask";
import { CaseMasterPublishing, isCaseMasterContest } from "screens/CreateCaseMasterContest";
import { createCaseMasterContest, deleteCaseMasterContest, getCaseMasterContests } from "services/caseMasterService";


const useStyle = makeStyles(theme => ({
  borderBottom: {
    borderBottom: "1px solid #8692A3",
    paddingBottom: theme.spacing(1.5),
    width: "100%",
    display : 'flex',
    flexDirection : 'column',
  },
  chipText: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 0.7,
  },
  cardImgContainer: {
    width: '32px', height: '32px',
    borderRadius: '8px',
    border: '1px solid #CCD4DE',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor : '#FFFFFF',
  },
  stickyColumnShadow: {
    boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1) !important',
    backgroundColor: '#FFFFFF !important', 
    position: 'sticky !important',
    left: '0 !important',
    zIndex: '1 !important',
  },
  container: {
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    display: 'flex',
    flexDirection : 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    padding: theme.spacing(10, 15),
    display: 'flex',
    gap: theme.spacing(2.5),
    flexDirection: 'column',
    width: '700px',
    background: '#FFFFFF',
    borderRadius: theme.spacing(2),
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
                0px 10px 20px 0px #CCD4DE4D,
                0px 7px 28px 0px #CCD4DE99`,
  },
}))

const Status = {
  live: { color: "#00664A", label: "Live", bgColor: '#DCFCE7' },
  expired: { color: "#C51407", label: "Expired", bgColor: '#FFE6E3' },
  unpublished: { color: "#BF7900", label: "Unpublished", bgColor: '#FFF6E5' },
};

const filters = [
  { field: "domain", type: "select", label: "Domain" },
  { field: 'batch', type: 'select', label: 'Batch' },
];

const codingFilters = [
  { field: 'batch', type: 'select', label: 'Batch' }
]

const shortlistFilters = [
  { field: 'batch', type: 'select', label: 'Batch' }
];

function StatusChip({ bgColor, color, label, tooltip }) {
  const classes = useStyle();
  

  return (
    <Box className={classes.chip} style={{ backgroundColor: bgColor || '#747775' , marginRight:'20px', 
      borderRadius:'16px' , padding:theme.spacing(0.25,2)
     }}>
      <BootstrapTooltip title={tooltip} >
        <Typography className={classes.chipText} color={color || 'black'}>
          {label || 'Label'}
        </Typography>
      </BootstrapTooltip>
    </Box>
  );
}

export default function PersonalizedInterviewList({refresh , setRefresh , activeTab}) {
  const classes = useStyle();
  const navigate= useNavigate();
  const snackbar = useSnackbar();
  const service = useServices();
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
  const [filterOptions, setFilterOptions] = React.useState({});
  const [openEditModal , setOpenEditModal] = React.useState(false)
  const [templateType , setTemplateType] = React.useState(TemplateType.MOCK)
  const [challengeId , setChallengeId] = React.useState(null)
  const [loading , setLoading] = React.useState(false)
  const [initialValues , setInitialValues] = React.useState( {
      name: '',
      batch: '',
      publishOn: '',
      expiresOn: '',
      maxAllowedAttempts: '1', 
      proctorSettings: {
        copyPasteProctoring: true,
        generalProctoring: true,
        imageCaptureProctoring: false,
      },
    })

  const [initialFilters, setInitialFilters] = React.useState({
    user: Session.userId,
    includeTotal: true,
    type: "personalized",
    orderBy: "updatedAt",
    order: "desc",
  });

  const [codingInitialFilters , setCodingInitialFilters] = React.useState({})

  const [shortlistInitialFilters, setShortlistInitialFilters] = React.useState({
    user : Session.userId
  });

  const [caseMasterInitialFilters] = React.useState({
    // Scope to this client. Without it the endpoint returns every client's
    // contests, which both leaks data and fills the first page with rows that
    // do not belong here.
    client: Session.userId,
    isActive: true,
    include: 'batch,casemasterProblem',
    // The server defaults to `_id asc` — oldest first — so a newly published
    // contest lands on the last page. Sort newest-first, and ask for the true
    // total so the pager can actually reach the later pages.
    orderBy: 'createdAt',
    order: 'desc',
    includeTotal: true,
  });

  const [caseMasterProblemId, setCaseMasterProblemId] = React.useState(null);

  const pageFilters = useEditorStore((state) => state.pageFilters)
  const setPageFilters = useEditorStore((state) => state.setPageFilters);
  const pageHistory = Session.pageHistory

  React.useEffect(() => {
    if (rowSelectionModel && rowSelectionModel.length > 0 && activeTab == 0) {
      navigate(`${rowSelectionModel[0]}/performance/next-round`);
    }else if (rowSelectionModel && rowSelectionModel.length > 0 && activeTab == 1){
      navigate(`${rowSelectionModel[0]}/performance/coding-round`);
    }
    else if (rowSelectionModel && rowSelectionModel.length > 0 && activeTab == 2){
      navigate(`${rowSelectionModel[0]}/performance/shortlist-genie`);
    }
    else if (rowSelectionModel && rowSelectionModel.length > 0 && activeTab == 3){
      navigate(`${rowSelectionModel[0]}/performance/case-round`);
    }
  }, [rowSelectionModel]);


  React.useEffect(() => {
    applyFiltersByHistory(pageFilters, setPageFilters, setInitialFilters);
  }, [pageHistory])

  React.useEffect(() => {
    if (activeTab === 0) {
      getDomains()
        .then(({ interviewDomains }) => {
          setFilterOptions((fO) => ({
            ...fO,
            domain: interviewDomains.map((d) => ({ id: d._id, label: d.name })),
          }));
        })
        .catch(console.error);
    } else {
      setFilterOptions((fO) => {
        const { domain, ...rest } = fO;
        return rest;
      });
      setPageFilters((prevFilters) => {
        const { domain, ...rest } = prevFilters;
        return rest;
      });
    }
  
    getBatches({ client: Session.userId })
      .then(({ batches }) => {
        setFilterOptions((fO) => ({
          ...fO,
          batch: batches.map((b) => ({ id: b._id, label: b.name })),
        }));
      })
      .catch(console.error);
  }, [activeTab]);

  const deleteCodingMock = async (id , type) => {
    try {
      let result;
      if(activeTab == 0){
        result = await service.callService(deleteDraftById , id);
      }else if (activeTab == 1 ){
        result = await service.callService(deleteChallenge, id);
      }else if (activeTab == 2){
        result = await service.callService(deleteShortlistTaskById , id);
      }else {
        result = await service.callService(deleteCaseMasterContest, id);
      }

      if (result) {
        setRefresh(true);
        if(type == TemplateType.CONTEST || activeTab == 3){
          snackbar.success('Contest deleted');
        }else{
          snackbar.success('Mock deleted');
        }
      }
    } catch (error) {
      console.error('Error deleting:', error);
      snackbar.error('Something went wrong while deleting.');
    }
  };

  const handleCaseMasterEdit = async (values) => {
    try {
      const result = await service.callService(createCaseMasterContest, {
        client: Session.userId,
        batch: values.batch,
        name: values.name,
        casemasterProblem: caseMasterProblemId,
        startAt: values.startAt,
        endAt: values.endAt,
        maxAllowedAttempts: values.maxAllowedAttempts,
        proctorSettings: values.proctorSettings,
      }, challengeId);

      if (result) {
        setRefresh(true);
        snackbar.success(
          `${isCaseMasterContest(values.maxAllowedAttempts) ? 'Contest' : 'Mock'} updated successfully!`
        );
        setOpenEditModal(false);
      }
    } catch (error) {
      snackbar.error(error?.message || 'Failed to update contest. Please try again.');
    }
  };

  const handleCodingEdit = async(values) => {
    try {
          const result = await service.callService(createChallenge, {
            type: templateType,
            batch: values.batch,
            user: Session.userId,
            settings: {
              name: values.name,
              publishOn: values.publishOn,
              expiresOn: values.expiresOn,
              maxAllowedAttempts: values.maxAllowedAttempts,
              proctorSettings: values.proctorSettings,
            },
          } , challengeId);
      
          if (result) {
            setRefresh(true);
            snackbar.success('Mock updated successfully!');
            setOpenEditModal(false)
          }
        } catch (error) {
          snackbar.error(error?.message || 'Failed to update challenge. Please try again.');
        }
  }

  const shortlistEdit = async(values) => {
    try {
      setLoading(true)
      const payload = {
        user: Session.userId,
        name: values.name,
        batch: values.batch,
        publishOn: values.publishOn,
        expiresOn: values.expiresOn,
      };
    
      if (values.jobRole) {
        payload.jobRole = values.jobRole;
      }
    
      if (values.company) {
        payload.company = values.company;
      }
    
      if (values.jobDescription) {
        payload.jobDescription = values.jobDescription;
      }
    
      const result = await service.callService(createShortlistTask, payload, challengeId);

      if (result) {
        setRefresh(true);
        snackbar.success('Task updated successfully!');
        setOpenEditModal(false)
        setLoading(false)
      }
      } catch (error) {
        snackbar.error(error?.message || 'Failed to update task. Please try again.');
        setLoading(false)
      }
  }

  const columns = [
    {
      field: 'name', label: 'Mock Inteview name', minWidth: 100,maxWidth : 220,
      sortBy: "data.INTERVIEW_DETAILS.name",
      disableSorting: true
    },
    {
      id: "status", field: "status", label: "Status", disableSorting: true,
      align: "center", minWidth: 130, marginRight:'10px',
      flex: 0, sortBy: "data.INTERVIEW_DETAILS.publishOn",
      renderCell: ({ row }) => {
        let status = Status.unpublished;
        let publishDate = row?.publishOn;
        let expireDate = row?.expiresOn;
        let tooltip = `Publising on ${formatDate(publishDate, 'DD/MM/YYYY')}`;
  
        if (publishDate && dayjs(publishDate).isBefore(new Date())) {
          status = Status.live;
          if (dayjs(expireDate).year() === 9999) {
            tooltip = `Never Expires`;
          }
          else {
            tooltip = `Expires on ${formatDate(expireDate, 'DD/MM/YYYY')}`;
          }
        }
  
        if (expireDate && dayjs(expireDate).isBefore(new Date())) {
          status = Status.expired;
          tooltip = `Expired on ${formatDate(expireDate, 'DD/MM/YYYY')}`;
        }
  
        return (
          <Box width='100%' display='flex' justifyContent='center'>
            <StatusChip
              color={status.color} bgColor={status.bgColor} label={status.label} tooltip={tooltip}
            />
          </Box>
        );
      },
    },
    {
      id: 'publishOn', field: 'publishOn', type: 'date',
      label: 'Published on', minWidth: 100, align: 'center',
      sortBy: "data.INTERVIEW_DETAILS.publishOn", disableSorting: true,
      renderCell: ({ row }) =>( 
        dayjs(row?.publishOn).isValid() ?
          formatDate(row?.publishOn, 'DD-MMM-YYYY') : '-'
      )
    },
    {
      id: 'attemptedBy', field: 'attemptedBy', type: '', disableSorting: true,
      label: 'Attempted by', minWidth: 100, align: 'center',
      renderCell: ({ row }) => {
        return row?.attemptedBy?.length || '0';
      }
    },
    {
      id: 'batch',
      label: 'Batch', minWidth: 100, maxWidth : 160,
      renderCell: ({ row }) => {
        return row?.batch?.name || '0';
      }
    },
    {
      id: 'company', field: 'company',
      label: 'Company name', minWidth: 100, maxWidth: 160,
      sortBy: "data.INTERVIEW_DETAILS.company", disableSorting: true,
      renderCell: ({ row }) => {
        const company = row?.company || '';
        const showTooltip = company.length > 20;
    
        const content = (
          <Typography
            noWrap
            sx={{
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {company}
          </Typography>
        );
    
        return showTooltip ? (
          <Tooltip title={company} arrow>
            {content}
          </Tooltip>
        ) : content;
      }
    },    
    {
      id: 'jobRole', field: 'jobRole',
      label: 'Job role', minWidth: 100, maxWidth: 160,
      sortBy: "data.INTERVIEW_DETAILS.jobRole",
      renderCell: ({ row }) => {
        const jobRole = row?.jobRole || '';
        const showTooltip = jobRole.length > 20; 
    
        const content = (
          <Typography
            noWrap
            sx={{
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {jobRole}
          </Typography>
        );
    
        return showTooltip ? (
          <Tooltip title={jobRole} arrow>
            {content}
          </Tooltip>
        ) : content;
      }
    },       
    {
      id: 'domain', field: 'domain',
      sortBy: "data.INTERVIEW_DETAILS.domain",
      label: 'Domain', minWidth: 100, maxWidth : 160,
      renderCell: ({ row }) => {
        return row?.domain?.name || '';
      }
    },
    {
      id: 'link', field: 'link', disableSorting: true,
      label: 'Invite link', minWidth: 100, align: 'center',
      renderCell: ({ row }) =>
        <IconButton size="small" onClick={() => {
          const link = `${process.env.REACT_APP_INPREP_URL}/${getClientPrefix()}/home?pid=${row._id}`;
          copyToClipboard(link);
          snackbar.success('Copied');
        }} >
          <ContentCopyIcon fontSize="small" color="primary" />
        </IconButton>,
    },
    {
      id: "analytics",
      label: "Analytics", fixed: true,
      minWidth: 120, disableSorting: true,
      align: "center",
      renderCell: ({ row }) =>
        <CustomIconButton  variant='contained'
          style={{
                    border : `1px solid ${theme.palette.primary.main}`,
                    padding : theme.spacing(1),
                    marginLeft : theme.spacing(9)
                }}
        >
          <img 
            src='https://assets.languify.in/images/PerformanceIcon.svg' 
            alt='performance' 
          />
        </CustomIconButton>,
    },
  ];

  const codingColumns = React.useMemo(() => ([
    {
      field: 'company',
      minWidth: 70,
      disableSorting: true,
      cellClassName: classes.stickyColumnShadow,
      headerClassName: classes.stickyColumnShadow,
      renderCell: ({ row }) => {
        const rawName = row?.details?.company?.name || 'DSA Challenge';
        const companyName = rawName
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        return (
          <Box className={classes.cardImgContainer}>
            <Tooltip title={companyName}>
              <img
                src={`https://assets.languify.in/images/${rawName.replace(/\s+/g, '_')}.png`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://assets.languify.in/images/companyTag.png";
                }}
                width={32}
                alt="logo"
                style={{ backgroundColor: '#FFFFFF', cursor: 'pointer' }}
              />
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: "name", 
      label: "Challenge", 
      minWidth: 150, 
      maxWidth: 200,
      disableSorting: true,
      renderCell: ({ row }) => row.details?.challenges?.[0]?.title || 'Unnamed',
    },
    { 
      field: "mock", 
      label: "Mock name", 
      minWidth: 150, 
      maxWidth: 200,
      disableSorting: true,
      renderCell: ({ row }) => row?.settings?.name || 'Unnamed',
    },
    {
      field: "type",
      label: "Type",
      minWidth: 100,
      align: "center",
      renderCell: ({ row }) => <span style={{ textTransform: 'capitalize'}}>{row.type || 'Mock'}</span>,
    },
    {
      field: "status",
      label: "Status",
      align: "center",
      minWidth: 130,
      flex: 0,
      renderCell: ({ row }) => {
        let status = Status.unpublished;
        let publishDate = row?.settings?.publishOn;
        let expireDate = row?.settings?.expiresOn;
        let tooltip = `Publishing on ${formatDate(publishDate, 'DD/MM/YYYY')}`;

        if (publishDate && dayjs(publishDate).isBefore(new Date())) {
          status = Status.live;
          if(dayjs(expireDate).year()===9999){
            tooltip = `Never Expires`;
          }else{
            tooltip = `Expires on ${formatDate(expireDate, 'DD/MM/YYYY')}`;
          }
        }
        if (expireDate && dayjs(expireDate).isBefore(new Date())) {
          status = Status.expired;
          tooltip = `Expired on ${formatDate(expireDate, 'DD/MM/YYYY')}`;
        }

        return (
          <Box width='100%' display='flex' justifyContent='center'>
            <StatusChip
              color={status.color} bgColor={status.bgColor} label={status.label} tooltip={tooltip}
            />
          </Box>
        );
      },
    },
    {
      field: "batch",
      label: "Batch",
      minWidth: 100, 
      maxWidth: 180,
      align: "center",
      renderCell: ({ row }) => row?.batch?.name || "-",
    },
    {
      field: "attemptedBy",
      label: "Attempted by",
      type: "number",
      renderCell: ({ row }) => row?.attemptedBy?.length || 0,
      align: "center",
    },
    {
      field: "publishedOn",
      label: "Published On", 
      type: 'date',
      minWidth: 116,
      align: "center",
      renderCell: ({ row }) => {
        const publishedOn = row?.settings?.publishOn;
        return publishedOn ? formatDate(publishedOn, 'DD-MMM-YYYY') : '-';
      },
    },
    {
      field: "expiresOn",
      label: "Expires On", 
      type: 'date',
      minWidth: 116,
      align: "center",
      renderCell: ({ row }) => {
        const expiresOn = row?.settings?.expiresOn;
        if (!expiresOn) return '-';
        
        const expireDate = dayjs(expiresOn);
        return expireDate.year() === 9999 ? 'Never Expires' : formatDate(expiresOn, 'DD-MMM-YYYY');
      },
    },
    {
      field: "link",
      label: "Invite Link", 
      fixed: true,
      minWidth: 120, 
      disableSorting: true,
      align: "center",
      renderCell: ({ row }) =>
        <IconButton size="small" onClick={(event) => {
          event.stopPropagation();
          const link = `${process.env.REACT_APP_INPREP_URL}/${getClientPrefix()}/home?cid=${row._id}`;
          copyToClipboard(link);
          snackbar.success('Copied');
        }} >
          <ContentCopyIcon fontSize="small" color="primary" />
        </IconButton>,
    },
    {
      id: "analytics",
      label: "Analytics", fixed: true,
      minWidth: 120, disableSorting: true,
      align: "center",
      renderCell: ({ row }) =>
        <CustomIconButton  variant='contained'
          style={{
                    border : `1px solid ${theme.palette.primary.main}`,
                    padding : theme.spacing(1),
                    marginLeft : theme.spacing(9)
                }}
        >
          <img 
            src='https://assets.languify.in/images/PerformanceIcon.svg' 
            alt='performance' 
          />
        </CustomIconButton>,
    },
  ]), []);

  const caseMasterColumns = React.useMemo(() => ([
    {
      field: 'name',
      label: 'Contest Name',
      minWidth: 150,
      maxWidth: 220,
      disableSorting: true,
      renderCell: ({ row }) => row?.name || 'Unnamed',
    },
    {
      field: 'case',
      label: 'Case',
      minWidth: 180,
      maxWidth: 240,
      disableSorting: true,
      renderCell: ({ row }) => row?.casemasterProblem?.title || '-',
    },
    {
      field: 'topic',
      label: 'Topic',
      minWidth: 130,
      disableSorting: true,
      renderCell: ({ row }) => {
        const topic = row?.casemasterProblem?.topic || '';
        return topic.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      },
    },
    {
      field: 'type',
      label: 'Type',
      minWidth: 100,
      align: 'center',
      disableSorting: true,
      // Case master has a single flow — one attempt means a contest, more than
      // one means repeatable practice. Fall back to 1 like the edit form does,
      // since that is the value the publish flow defaults to.
      renderCell: ({ row }) =>
        isCaseMasterContest(row?.maxAllowedAttempts ?? 1) ? 'Contest' : 'Practice',
    },
    {
      field: 'status',
      label: 'Status',
      align: 'center',
      minWidth: 130,
      flex: 0,
      disableSorting: true,
      renderCell: ({ row }) => {
        let status = Status.unpublished;
        let startDate = row?.startAt;
        let endDate = row?.endAt;
        let tooltip = `Starts on ${formatDate(startDate, 'DD/MM/YYYY')}`;

        if (startDate && dayjs(startDate).isBefore(new Date())) {
          status = Status.live;
          tooltip = `Ends on ${formatDate(endDate, 'DD/MM/YYYY')}`;
        }
        if (endDate && dayjs(endDate).isBefore(new Date())) {
          status = Status.expired;
          tooltip = `Ended on ${formatDate(endDate, 'DD/MM/YYYY')}`;
        }

        return (
          <Box width='100%' display='flex' justifyContent='center'>
            <StatusChip color={status.color} bgColor={status.bgColor} label={status.label} tooltip={tooltip} />
          </Box>
        );
      },
    },
    {
      field: 'batch',
      label: 'Batch',
      minWidth: 100,
      maxWidth: 180,
      align: 'center',
      renderCell: ({ row }) => row?.batch?.name || '-',
    },
    {
      field: 'attemptedBy',
      label: 'Attempted by',
      type: 'number',
      minWidth: 120,
      align: 'center',
      disableSorting: true,
      renderCell: ({ row }) => row?.attemptedBy?.length || 0,
    },
    {
      field: 'startAt',
      label: 'Start At',
      type: 'date',
      minWidth: 116,
      align: 'center',
      disableSorting: true,
      renderCell: ({ row }) => row?.startAt ? formatDate(row.startAt, 'DD-MMM-YYYY') : '-',
    },
    {
      field: 'endAt',
      label: 'End At',
      type: 'date',
      minWidth: 116,
      align: 'center',
      disableSorting: true,
      renderCell: ({ row }) => row?.endAt ? formatDate(row.endAt, 'DD-MMM-YYYY') : '-',
    },
  ]), []);

  const shortlistingColumns = React.useMemo(() => ([
    {
      field: "name", 
      label: "Shortlisting task", 
      minWidth: 150, 
      maxWidth: 200,
      disableSorting: true,
    },
    { 
      field: "type", 
      label: "Type", 
      minWidth: 150, 
      maxWidth: 200,
      disableSorting: true,
      renderCell: ({ row }) => row?.company ? 'Company specific' : 'Job role specific',
    },
    {
      field: "status",
      label: "Status",
      align: "center",
      minWidth: 130,
      flex: 0,
      renderCell: ({ row }) => {
        let status = Status.unpublished;
        let publishDate = row?.publishOn;
        let expireDate = row?.expiresOn;
        let tooltip = `Publishing on ${formatDate(publishDate, 'DD/MM/YYYY')}`;

        if (publishDate && dayjs(publishDate).isBefore(new Date())) {
          status = Status.live;
          if(dayjs(expireDate).year()===9999){
            tooltip = `Never Expires`;
          }else{
            tooltip = `Expires on ${formatDate(expireDate, 'DD/MM/YYYY')}`;
          }
        }
        if (expireDate && dayjs(expireDate).isBefore(new Date())) {
          status = Status.expired;
          tooltip = `Expired on ${formatDate(expireDate, 'DD/MM/YYYY')}`;
        }

        return (
          <Box width='100%' display='flex' justifyContent='center'>
            <StatusChip
              color={status.color} bgColor={status.bgColor} label={status.label} tooltip={tooltip}
            />
          </Box>
        );
      },
    },
    {
      field: "batch",
      label: "Batch",
      minWidth: 100, 
      maxWidth: 180,
      align: "center",
      renderCell: ({ row }) => row?.batch?.name || "-",
    },
    {
      field: "attemptedBy",
      label: "Attempted by",
      type: "number",
      renderCell: ({ row }) => row?.attemptedBy?.length || 0,
      align: "center",
    },
    {
      field: "publishedOn",
      label: "Published On", 
      type: 'date',
      minWidth: 116,
      align: "center",
      renderCell: ({ row }) => {
        const publishedOn = row?.publishOn;
        return publishedOn ? formatDate(publishedOn, 'DD-MMM-YYYY') : '-';
      },
    },
    {
      field: "expiresOn",
      label: "Expires On", 
      type: 'date',
      minWidth: 116,
      align: "center",
      renderCell: ({ row }) => {
        const expiresOn = row?.expiresOn;
        if (!expiresOn) return '-';
        
        const expireDate = dayjs(expiresOn);
        return expireDate.year() === 9999 ? 'Never Expires' : formatDate(expiresOn, 'DD-MMM-YYYY');
      },
    },
    {
      id: "analytics",
      label: "Analytics", fixed: true,
      minWidth: 120, disableSorting: true,
      align: "center",
      renderCell: ({ row }) =>
        <CustomIconButton  variant='contained'
          style={{
                    border : `1px solid ${theme.palette.primary.main}`,
                    padding : theme.spacing(1),
                    marginLeft : theme.spacing(9)
                }}
        >
          <img 
            src='https://assets.languify.in/images/PerformanceIcon.svg' 
            alt='performance' 
          />
        </CustomIconButton>,
    },
  ]), []);
  

  return (
    <Box sx={{backgroundColor : '#FFFFFF' , padding : '48px 40px' , marginTop : '16px'}}>
      <Box className={classes.borderBottom}>
        <Typography variant="h4-medium">
          {activeTab === 0 ? 'Published Next Round Mocks'
            : activeTab === 1 ? 'Published Coding Mocks & Contests'
            : activeTab === 2 ? 'Shortlisting tasks'
            : 'Published Case Master Mocks & Contests'}
        </Typography>
        <Typography variant="body01-bold" color='neutral.clr-600'>
          {activeTab === 0 ? 'Track all Next Round Mocks published across batches'
            : activeTab === 1 ? 'Track all Coding Mocks and Contests published across batches'
            : activeTab === 2 ? 'Track all shortlisting tasks published across batches'
            : 'Track and manage all Case Master mocks and contests published across batches.'}
        </Typography>
      </Box>
      <Paper elevation={0} style={{ width: "100%" , marginTop : '12px' }}>
        <DynamicTable
          key={activeTab}
          hideDeleteAction
          entity="Draft"
          noRowsMessage={activeTab === 0 ? 'You have not published any mock interview for any batch so far! Once published you can see in this section'
            : activeTab === 1 ? 'You have not published any coding mock for any batch so far! Once published you can see in this section'
            : activeTab === 2 ? 'You have not published any shortlisting task for any batch so far! Once published you can see in this section'
            : 'You have not published any Case Master Contest for any batch so far! Once published you can see in this section'}
          columns={activeTab === 0 ? columns : activeTab === 1 ? codingColumns : activeTab === 2 ? shortlistingColumns : caseMasterColumns}
          filters={
            activeTab === 0
              ? filters
              : activeTab === 1
                ? codingFilters
                : activeTab === 2
                  ? shortlistFilters
                  : codingFilters
          }
          filterOptions={filterOptions}
          sx={{ p: 0 }}
          useListProps={{
            getItems: activeTab === 0 ? getPersonalizedInterview : activeTab === 1 ? getCodingInterviews : activeTab === 2 ? getShortlistingTasks : getCaseMasterContests,
            itemName: activeTab === 0 ? 'templates' : activeTab === 1 ? null : activeTab === 2 ? 'shortlistTasks' : 'casemasterContests',
            initialFilters: activeTab === 0
              ? initialFilters
              : activeTab === 1
                ? codingInitialFilters
                : activeTab === 2
                  ? shortlistInitialFilters
                  : caseMasterInitialFilters,
          }}
          rowSelectionModel={rowSelectionModel}
          setRowSelectionModel={setRowSelectionModel}
          personalizedList={true}
          refreshData={refresh}
          setRefresh={setRefresh}
          menuOptions={[
            {
              id: 'delete',
              label: 'Delete',
              onClick: (event, params) => {
                event.stopPropagation();
        
                let publishDate, expireDate;
                const now = dayjs();

                if (activeTab === 0 || activeTab === 2) {
                  publishDate = params?.row?.publishOn;
                  expireDate = params?.row?.expiresOn;
                } else if (activeTab === 1) {
                  publishDate = params?.row?.settings?.publishOn;
                  expireDate = params?.row?.settings?.expiresOn;
                } else {
                  publishDate = params?.row?.startAt;
                  expireDate = params?.row?.endAt;
                }

                const isLive =
                  publishDate && dayjs(publishDate).isBefore(now) &&
                  (!expireDate || dayjs(expireDate).isAfter(now));
                const isExpired = expireDate && dayjs(expireDate).isBefore(now);

                if (isLive || isExpired) {
                  if (activeTab === 0) {
                    snackbar.error("Deletion is not allowed for live or expired mock interview.");
                  } else if (activeTab === 1) {
                    snackbar.error("Deletion is not allowed for live or expired mock interview or contest.");
                  } else if (activeTab === 2) {
                    snackbar.error("Deletion is not allowed for live or expired shortlist tasks.");
                  } else {
                    snackbar.error("Deletion is not allowed for live or expired Case Master contests.");
                  }
                  return;
                }
        
                deleteCodingMock(params.id , params?.row?.type);
              },
            },
            ...(activeTab !== 0
              ? [
                {
                  id: 'edit',
                  label: 'Edit',
                  onClick: (event, params) => {
                    event.stopPropagation();

                    const row = params.row;

                    if(activeTab === 1){
                      setInitialValues({
                        name: row?.settings?.name || '',
                        batch: row?.batch?._id || '',
                        publishOn: row?.settings?.publishOn || '',
                        expiresOn: row?.settings?.expiresOn || '',
                        maxAllowedAttempts: row?.settings?.maxAllowedAttempts || '1',
                        proctorSettings: {
                          copyPasteProctoring: row?.settings?.proctorSettings?.copyPasteProctoring ?? true,
                          generalProctoring: row?.settings?.proctorSettings?.generalProctoring ?? true,
                          imageCaptureProctoring: row?.settings?.proctorSettings?.imageCaptureProctoring ?? false,
                        },
                      });
                      setTemplateType(row?.type)
                      setChallengeId(row?._id)
                      setOpenEditModal(true);
                    } else if (activeTab === 3) {
                      setInitialValues({
                        name: row?.name || '',
                        batch: row?.batch?._id || '',
                        startAt: row?.startAt || '',
                        endAt: row?.endAt || '',
                        maxAllowedAttempts: row?.maxAllowedAttempts || '1',
                        proctorSettings: {
                          copyPasteProctoring: row?.proctorSettings?.copyPasteProctoring ?? true,
                          generalProctoring: row?.proctorSettings?.generalProctoring ?? true,
                          imageCaptureProctoring: row?.proctorSettings?.imageCaptureProctoring ?? false,
                        },
                      });
                      setCaseMasterProblemId(row?.casemasterProblem?._id || row?.casemasterProblem);
                      setChallengeId(row?._id);
                      setOpenEditModal(true);
                    } else if (activeTab === 2){
                      const initialValues = {
                        name: row?.name || '',
                        batch: row?.batch?._id || '',
                        publishOn: row?.publishOn || '',
                        expiresOn: row?.expiresOn || '',
                        jobRole: row?.jobRole || ''
                      };
                      
                      if (row?.company) {
                        initialValues.company = row.company;
                      }
                      
                      if (row?.jobDescription) {
                        initialValues.jobDescription = row.jobDescription;
                      }
                      
                      setInitialValues(initialValues);
                      
                      setChallengeId(row?._id)
                      setOpenEditModal(true);
                    }
                
                  },
                }                
                ]
              : []),
          ]}
          
        />
      </Paper>
      <Dialog 
        open={openEditModal} 
        onClose={()=> setOpenEditModal(false)}  
        maxWidth='700px' 
        width='100%'  
        scroll="paper"
        sx={{
          '& .MuiBackdrop-root': {
             backgroundColor: 'rgba(0, 17, 38, 0.7)',
             backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
          },
        }}
      >
         <IconButton
            onClick={()=> setOpenEditModal(false)}
            sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1,
                backgroundColor: 'white',
                '&:hover': {
                backgroundColor: '#f0f0f0',
                }
            }}
        >
            <CloseIcon />
        </IconButton>
        <Box display='flex' flexDirection='column'>
                <Box className={classes.container}>
                  {activeTab === 1 ? (
                    <Box className={classes.box}>
                      <Header onBack={()=> setOpenEditModal(false)} currentStep={MockPublishingSteps.PUBLISHING_PROCTORING} />
                      <Proctorship
                        onFinish={handleCodingEdit}
                        templateType={templateType}
                        initialValues={initialValues}
                        editPage={true}
                      />
                    </Box>
                  ) : activeTab === 3 ? (
                    <Box className={classes.box}>
                      <Header onBack={() => setOpenEditModal(false)} currentStep={CaseMasterPublishingSteps.CASE_MASTER_PUBLISHING} />
                      <CaseMasterPublishing
                        onFinish={handleCaseMasterEdit}
                        initialValues={initialValues}
                        editPage={true}
                      />
                    </Box>
                  ) : (
                    <Box className={classes.box}>
                      <ShortlistHeader onBack={()=> setOpenEditModal(false)} title='Setup Publishing' />
                      <PublishingComponent
                        onPublish={shortlistEdit}
                        initialValues={initialValues}
                        loading={loading}
                        editPage={true}
                      />
                    </Box>
                  )}
                  
                </Box>   
              </Box>
        
      </Dialog>
      
    </Box>
  )
}


